import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { DeviceStatusBadge } from '../shared/StatusBadge';
import type { SerializedItem, DeviceStatus, ReasonCode, ChangeDeviceStatusInput } from '../../types';

interface DeviceStatusChangeModalProps {
  open: boolean;
  onClose: () => void;
  onChangeStatus: (data: ChangeDeviceStatusInput) => Promise<void>;
  item: SerializedItem | null;
  isLoading?: boolean;
}

const deviceStatusOptions: { value: DeviceStatus; label: string; description: string }[] = [
  { value: 'valid', label: 'Valid', description: 'Production-ready, calibrated' },
  { value: 'verify', label: 'Verify', description: 'Needs verification/inspection' },
  { value: 'test', label: 'Test', description: 'Under testing' },
  { value: 'dev', label: 'Dev', description: 'Development/prototype' },
  { value: 'dead', label: 'Dead', description: 'Non-functional, for scrap/RMA' },
];

const reasonOptions: { value: ReasonCode; label: string }[] = [
  { value: 'qc_pass', label: 'QC Passed' },
  { value: 'qc_fail', label: 'QC Failed' },
  { value: 'damage', label: 'Damage' },
  { value: 'rma', label: 'RMA' },
  { value: 'correction', label: 'Correction' },
  { value: 'production', label: 'Production' },
  { value: 'other', label: 'Other' },
];

export function DeviceStatusChangeModal({
  open,
  onClose,
  onChangeStatus,
  item,
  isLoading,
}: DeviceStatusChangeModalProps) {
  const [newStatus, setNewStatus] = useState<DeviceStatus | ''>('');
  const [reason, setReason] = useState<ReasonCode | ''>('');
  const [notes, setNotes] = useState('');

  // Reset when modal opens with new item
  useEffect(() => {
    if (open && item) {
      setNewStatus('');
      setReason('');
      setNotes('');
    }
  }, [open, item]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newStatus || !reason) return;

    try {
      await onChangeStatus({
        device_status: newStatus,
        reason: reason,
        notes: notes || undefined,
      });
      toast.success('Device status updated successfully');
      onClose();
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Unknown error occurred';
      toast.error(`Failed to update device status: ${message}`);
      console.error('Error updating device status:', error);
      // Keep form open for retry
    }
  };

  if (!item) return null;

  const availableStatuses = deviceStatusOptions.filter(
    (opt) => opt.value !== item.device_status
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Change Device Status</DialogTitle>
          <DialogDescription>
            Update the 5S status for this device. This action is logged.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Item Info */}
          <div className="p-3 bg-muted rounded-lg space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Serial Number</span>
              <span className="font-mono font-medium">{item.serial_number}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Current Status</span>
              {item.device_status ? (
                <DeviceStatusBadge status={item.device_status} />
              ) : (
                <span className="text-muted-foreground">Not set</span>
              )}
            </div>
          </div>

          {/* New Status */}
          <div>
            <label className="text-sm font-medium">New Status *</label>
            <Select value={newStatus} onValueChange={(v) => setNewStatus(v as DeviceStatus)}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select new status" />
              </SelectTrigger>
              <SelectContent>
                {availableStatuses.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex flex-col">
                      <span className="font-medium">{option.label}</span>
                      <span className="text-xs text-muted-foreground">{option.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Reason */}
          <div>
            <label className="text-sm font-medium">Reason *</label>
            <Select value={reason} onValueChange={(v) => setReason(v as ReasonCode)}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select reason" />
              </SelectTrigger>
              <SelectContent>
                {reasonOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div>
            <label className="text-sm font-medium">Notes</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes about this status change..."
              className="mt-1"
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || !newStatus || !reason}
            >
              {isLoading ? 'Updating...' : 'Update Status'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
